/* ==========================================================================
   Noveliya — Maatwerkconfigurator

   Model: DRAFT (the radios and checkbox inside the dialog) versus COMMITTED
   (this.committed). Only a committed choice reaches the cart. Closing the
   dialog any other way than "Bevestigen" throws the draft away.

   Consequences that were asked for, and where they live:
   - change colour after confirming  → uncheckConsent() in onColourChange()
   - overlay, not inline             → this.dialog.showModal()
   - click outside closes it         → backdrop click handler
   - closes when finished            → commit() calls dialog.close()
   ========================================================================== */

if (!customElements.get('nv-configurator')) {
  customElements.define(
    'nv-configurator',
    class NvConfigurator extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;

        this.dialog = this.querySelector('[data-nv-dialog]');
        this.colours = Array.from(this.querySelectorAll('[data-nv-colour]'));
        this.consent = this.querySelector('[data-nv-consent]');
        this.confirmButton = this.querySelector('[data-nv-confirm]');
        this.recheck = this.querySelector('[data-nv-recheck]');

        this.openButton = this.querySelector('[data-nv-open]');
        this.rowValue = this.querySelector('[data-nv-rowvalue]');
        this.dot = this.querySelector('[data-nv-dot]');

        this.propColour = this.querySelector('[data-nv-prop-colour]');
        this.propConsent = this.querySelector('[data-nv-prop-consent]');

        /* Nothing is chosen yet. `hadConsent` remembers that the customer had
           already ticked once, so we can explain why the tick disappeared
           instead of silently resetting it. */
        this.committed = { colour: '', hex: '', consent: false };
        this.hadConsent = false;

        this.openButton?.addEventListener('click', () => this.open());
        this.querySelector('[data-nv-close]')?.addEventListener('click', () => this.dialog.close());
        this.confirmButton?.addEventListener('click', () => this.commit());
        this.colours.forEach((input) => input.addEventListener('change', () => this.onColourChange()));
        this.consent?.addEventListener('change', () => this.syncDraft());

        /* Light dismiss. The click lands on the <dialog> itself only when it
           misses the panel — which is the backdrop. TRAP: this needs the dialog
           to have zero padding, otherwise its own padding counts as "outside"
           and a near-miss inside the box closes it. See the CSS. */
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        /* Fires for Esc, backdrop, close button and commit alike. Rendering
           from `committed` here is what discards an unconfirmed draft. */
        this.dialog?.addEventListener('close', () => {
          document.body.classList.remove('nv-scroll-locked');
          this.render();
          this.openButton?.focus();
        });

        /* This product IS the custom configurator product — always active. */
        this.setActive(true);
      }

      /* Dawn replaces the buy-buttons markup on every variant change, so a
         cached reference goes stale and we would be styling a detached node.
         Look it up each time. */
      get submitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      /* ── Variant gate ─────────────────────────────────────────────────── */

      setActive(isActive) {
        this.hidden = !isActive;

        if (!isActive) {
          if (this.dialog?.open) this.dialog.close();
          this.committed = { colour: '', hex: '', consent: false };
          this.hadConsent = false;
          this.colours.forEach((i) => {
            i.disabled = true;
            i.checked = false;
          });
          if (this.consent) {
            this.consent.disabled = true;
            this.consent.checked = false;
          }
          this.writeProps();
          this.toggleSubmit(false);
          return;
        }

        this.colours.forEach((i) => (i.disabled = false));
        if (this.consent) this.consent.disabled = false;
        this.render();
      }

      /* ── Dialog ───────────────────────────────────────────────────────── */

      open() {
        /* Seed the draft from what was last confirmed, so reopening shows the
           customer their own choice rather than a blank card. */
        this.colours.forEach((i) => (i.checked = i.value === this.committed.colour));
        if (this.consent) this.consent.checked = this.committed.consent;
        if (this.recheck) this.recheck.hidden = true;

        this.syncDraft();
        document.body.classList.add('nv-scroll-locked');
        this.dialog.showModal();

        /* Focus the chosen colour, or the first one. showModal() would
           otherwise land focus on the close button, which reads as "leave". */
        const target = this.colours.find((i) => i.checked) || this.colours[0];
        target?.focus({ preventScroll: true });
      }

      onColourChange() {
        /* THE RULE: consent is given for one specific colour. Pick another and
           the tick is void — the customer confirms again or does not buy. */
        if (this.consent?.checked) {
          this.consent.checked = false;
          this.hadConsent = true;
        }
        if (this.recheck) this.recheck.hidden = !this.hadConsent;
        this.syncDraft();
      }

      get draft() {
        const picked = this.colours.find((i) => i.checked);
        return {
          colour: picked?.value || '',
          hex: picked?.dataset.hex || '',
          consent: !!this.consent?.checked,
        };
      }

      syncDraft() {
        const { colour, consent } = this.draft;
        if (this.confirmButton) this.confirmButton.disabled = !(colour && consent);
        if (consent && this.recheck) this.recheck.hidden = true;
      }

      commit() {
        const { colour, hex, consent } = this.draft;
        if (!colour || !consent) return;
        this.committed = { colour, hex, consent };
        this.hadConsent = true;
        this.dialog.close(); // the close handler calls render()
      }

      /* ── Rendering the committed state ────────────────────────────────── */

      render() {
        const { colour, hex, consent } = this.committed;
        const done = Boolean(colour && consent);

        if (this.rowValue) this.rowValue.textContent = done ? colour : this.dataset.labelEmpty;
        if (this.openButton) {
          this.openButton.textContent = done ? this.dataset.labelChange : this.dataset.labelOpen;
          this.openButton.classList.toggle('nv-config__open--quiet', done);
        }
        if (this.dot) {
          this.dot.style.setProperty('--nv-col', hex || 'transparent');
          this.dot.classList.toggle('is-set', done);
        }

        this.writeProps();
        this.toggleSubmit(!done);
      }

      writeProps() {
        const { colour, consent } = this.committed;
        const done = Boolean(colour && consent);
        this.setProp(this.propColour, done ? colour : '');
        this.setProp(this.propConsent, done ? 'Ja — akkoord dat maatwerk niet retour kan' : '');
      }

      setProp(input, value) {
        if (!input) return;
        input.value = value;
        /* Disabled inputs are not submitted — that is how a plain Ivory order
           avoids an empty "Kleur:" line. */
        input.disabled = !value;
      }

      toggleSubmit(disable) {
        const button = this.submitButton;
        if (!button) return;
        /* aria-disabled, not the disabled attribute: Dawn's product-form owns
           `disabled` for sold-out state and would fight us over it. Its submit
           handler already bails on aria-disabled="true". */
        button.setAttribute('aria-disabled', String(disable));
        button.classList.toggle('is-blocked', disable);
      }

      disconnectedCallback() {
        document.body.classList.remove('nv-scroll-locked');
      }
    }
  );
}
